import { usePermissions } from '@refinedev/core'
import { Alert, Breadcrumb, Button, Card, Form, Input } from 'antd'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { PermissionPicker } from './components/PermissionPicker'
import { usePermissionScopes } from './hooks/usePermissionScopes'
import { useRoleActions } from './hooks/useRoleActions'
import { useRoleConfirm } from './hooks/useRoleConfirm'

interface FormValues {
  name: string
  description?: string
}

export default function RoleCreatePage() {
  const [form] = Form.useForm<FormValues>()
  const navigate = useNavigate()
  const { createRole } = useRoleActions()
  const { confirmCreate } = useRoleConfirm()
  const { data: callerPermissions = [] } = usePermissions<string[]>({})
  const { scopes, isLoading, error, refetch } = usePermissionScopes()
  const [permissions, setPermissions] = useState<string[]>([])

  const handleSubmit = async () => {
    let values: FormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }

    confirmCreate(values.name, permissions.length, async () => {
      await createRole(values.name, values.description, permissions)
      void navigate('/roles')
    })
  }

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[{ title: <Link to="/roles">Roles</Link> }, { title: 'Create' }]}
      />
      <h2 style={{ marginBottom: 24 }}>Create Role</h2>
      <Card style={{ maxWidth: 720 }}>
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Role Name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input placeholder="Enter role name" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea placeholder="Optional description" rows={3} />
          </Form.Item>
          <Form.Item label="Permissions">
            {error && (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
                message="Failed to load permission scopes."
                action={<Button onClick={() => refetch()}>Retry</Button>}
              />
            )}
            <PermissionPicker
              scopes={scopes}
              value={permissions}
              onChange={setPermissions}
              callerPermissions={callerPermissions}
              loading={isLoading}
              disabled={Boolean(error)}
            />
          </Form.Item>
          <Button
            type="primary"
            onClick={handleSubmit}
            disabled={Boolean(error)}
          >
            Create Role
          </Button>
        </Form>
      </Card>
    </div>
  )
}
